import { ProjectFile } from '../../core/hp/projectFile';
import type { Level, ModelRef, Scene } from '../../core/hp/types';
import { translate } from '../../i18n/I18n';
import type { EditorState, Store } from '../../state/store';
import { rebaseItemsPatch, recordedChange } from '../patch';
import type { CommitDecision, DocumentOwner, DocumentPatch, ItemChange, ItemIdentity, LocalProposal, NewItemOrigin, SharedStoreAdapter } from '../protocol';
import { shareEqual } from '../structuralSharing';
import type { RtClock, RtConnection, RtConnectionTerminal, RtStatus, RtVisibility } from './connection';
import { CHANGED_IN_APP, encodeProposal, furnitureIdOf, type GraveyardEntry, type ItemOrigin } from './outgoing';
import { LivePresence } from './livePresence';
import { applyPeerEvents, type PeerDivergence } from './peerEvents';
import type { RtEvent } from './wire';

export type LiveTerminal = RtConnectionTerminal | 'unreadable';
/**
 * `idless`: the room's file has objects without a UniqueId, which an edit cannot name, until the app saves the project.
 * `checking`: the person asked whether it has been saved since, and the room's fresh copy is not in yet.
 * `faithful`: nothing recorded since the base, at the app's file version: only such a copy answers rt and is handed over.
 * `otherVersion`: the base is not at the app's file version, so the copy is not faithful until the next base whatever follows.
 * `roleChange`: the person's edit rights changed, to be told until `acknowledgeRole` (the app's MultiplayerPermissionsChangedPopup).
 */
export type LiveState = { status: RtStatus; role: string | null; terminal: LiveTerminal | null; idless: boolean; checking: boolean; faithful: boolean; otherVersion: boolean;
  roleChange: 'view' | 'edit' | null };
type DevWindow = Window & { hpLive?: unknown };
export interface LiveSessionDeps {
  store: Store;
  projectId: string;
  name: string;
  connection: Pick<RtConnection, 'on' | 'start' | 'retry' | 'resync' | 'close' | 'send' | 'canSend'>;
  clock: Pick<RtClock, 'now' | 'setTimeout' | 'clearTimeout'>;
  /** The scene of the stored file, with internal ids that survive a re-read (`stableIds`). */
  parse: (xml: string) => Scene;
  /** Runs `run` on the next frame: every message of one frame is one parse. */
  schedule: (run: () => void) => void;
  /** Fetches what the parse needs of models it has not seen (sizes, pivots, tags); a re-read follows. */
  prepareModels: (models: ModelRef[]) => Promise<void>;
  /** Told once, when the room's scene is first in the store. */
  onOpen?: () => void;
  /** The parse's own `catalogPivots`: a piece added on a wall is placed by the same pivot the scene shows it with. */
  pivots?: () => ReadonlyMap<string, number>;
  /** Whether the parse has the model's measured size and pivot. */
  measured?: (modelId: string) => boolean;
  /** True once the web queue's answer changes for a model it is still converting; false at once otherwise, and as `signal` aborts. */
  converting?: (modelId: string, signal?: AbortSignal) => Promise<boolean>;
  /** The page hidden, maybe for good (`pagehide`); the window's by default. */
  pageHide?: Pick<RtVisibility, 'subscribe'>;
  /** Pointers held down on the page, told as one lifts; the window's by default. */
  pointers?: PointerHold;
}
export interface PointerHold { held(): boolean; subscribe(fn: () => void): () => void }

const windowPageHide: Pick<RtVisibility, 'subscribe'> | null = typeof window === 'undefined' ? null : {
  subscribe: fn => { window.addEventListener('pagehide', fn); return () => window.removeEventListener('pagehide', fn); },
};

/**
 * The app's `IsGestureLive` (`GestureSystem.TouchCount > 0`): any pointer down, counted from `target`'s capture phase.
 * Safari sends no pointercancel when a native drag starts: its end, or the pointer moving with no button, lifts it.
 */
export function pointerHold(target: EventTarget | null = typeof window === 'undefined' ? null : window): PointerHold | null {
  if (!target) return null;
  const down = new Set<number>(), listeners = new Set<() => void>();
  const tell = () => { for (const fn of [...listeners]) fn(); };
  const press = (event: Event) => { down.add((event as PointerEvent).pointerId); };
  const lift = (event: Event) => { if (down.delete((event as PointerEvent).pointerId)) tell(); };
  const hover = (event: Event) => { if ((event as PointerEvent).buttons === 0) lift(event); };
  const lost = () => { if (!down.size) return; down.clear(); tell(); };
  const types: Array<[string, (event: Event) => void]> = [['pointerdown', press], ['pointerup', lift], ['pointercancel', lift], ['pointermove', hover],
    ['pointerover', hover], ['dragend', lost], ['drop', lost]];
  return {
    held: () => down.size > 0,
    subscribe: fn => {
      // The window's own blur only: in the capture phase every control losing focus would read as a lift.
      if (!listeners.size) { for (const [type, handler] of types) target.addEventListener(type, handler, true); target.addEventListener('blur', lost); }
      listeners.add(fn);
      return () => {
        listeners.delete(fn);
        if (listeners.size) return;
        for (const [type, handler] of types) target.removeEventListener(type, handler, true);
        target.removeEventListener('blur', lost);
        down.clear();
      };
    },
  };
}

/** Ends the person may try again from; the others are final. */
const RECOVERABLE: ReadonlySet<LiveTerminal> = new Set(['superseded', 'full', 'unreachable']);
const WRITERS: ReadonlySet<string> = new Set(['owner', 'can_edit']);
const ORIGINS_SIZE = 256;
/** An unknown id may come before the app's own `UpdateState` with ids: one re-sync waits for its batch to settle. */
const RESYNC_DELAY_MS = 2000;
/** How often a fresh copy rt's save asked for, held back by the person's own work, looks again. */
const REFRESH_POLL_MS = 250;
const SLOW_PARSE_MS = 50, SLOW_PARSE_GAP_MS = 250;
/** `Project.CurrentRevision`: the app migrates an older file whole on open (`Backward`), so a file at another version is not what it saves. */
const APP_REVISION = 20;
const PRESENCE: ReadonlySet<string> = new Set(['Move', 'DomainSelection', 'PlayerJoined', 'PlayerLeft']);

function alignById<T extends { id: string }>(previous: T[], next: T[]): T[] {
  if (previous.length === next.length && previous.every((value, index) => value.id === next[index].id)) return previous;
  const byId = new Map(previous.map(value => [value.id, value]));
  return next.map(value => byId.get(value.id) ?? value);
}

/** A selection the editor opens its properties panel for (EditorView `inspectorOpen`): a rejoin closes it, typed text and all. */
function inspected(state: EditorState): boolean {
  const kind = state.selection?.kind;
  return !!kind && kind !== 'vertex' && kind !== 'items';
}

function bounded<K, V>(map: Map<K, V>, key: K, value: V, size: number): void {
  map.delete(key);
  map.set(key, value);
  if (map.size > size) map.delete(map.keys().next().value!);
}

/** The words' argument naming the pieces an edit left as they are (`Encoded.held`, `Refusal.pieces`). */
function naming(pieces?: readonly string[]): { args?: string[] } {
  if (!pieces?.length) return {};
  return { args: [[...new Set(pieces.map(caption => caption || translate('#ToolbarItem')))].join(', ')] };
}

/** Editing gone, for the link, the role, the session or a demo guest's view: the selection goes and every item panel closes, with any draft in one. */
export function closeItemPanels(store: Pick<Store, 'set'>, extra: Partial<EditorState> = {}): void {
  store.set({ propertiesPreview: null, selection: null, selectionAnchor: null, showProperties: false, showVariations: false, showCatalog: false,
    materialTarget: null, resizeMode: false, saveError: null, ...extra });
}

/**
 * The web in the app's live room: the scene is read again from the stored file and enters the store as its shared
 * adapter, so the browser keeps no copy. An owner or editor on a live link changes the room's items: each edit becomes the
 * app's own Domain events, applied to the stored file, sent as one batch and read back before the store hears the answer.
 * A faithful copy answers rt's pull with the stored file and hands it over as the web leaves; a copy that is not faithful
 * takes rt's freshly saved one after its next save.
 */
export class LiveSession implements SharedStoreAdapter {
  readonly localCopy = false;
  /** The room's other players, and the web's own Move. */
  readonly presence: LivePresence;
  private state: LiveState = { status: 'connecting', role: null, terminal: null, idless: false, checking: false, faithful: false, otherVersion: false, roleChange: null };
  private listeners = new Set<() => void>();
  private stops: Array<() => void> = [];
  private file: ProjectFile | null = null;
  /** Bumped by each base and by close: work begun for an older base lands nowhere. */
  private base = 0;
  private revision = 0;
  private opened = false;
  private opening = false;
  private closed = false;
  private parseQueued = false;
  private lastParseAt = -Infinity;
  private lastParseMs = 0;
  private resyncTimer: unknown = null;
  private refreshTimer: unknown = null;
  /** A role change waiting for the gesture under way to end: what to tell then, and whether to ask for a fresh copy. */
  private roleWait: { tell: 'view' | 'edit'; resync: boolean; queued: boolean; timer: unknown } | null = null;
  private pointers: PointerHold | null = null;
  /** A fresh copy was taken after rt's save for a base at another file version: while bases come at that version, its saver writes it. */
  private versionAsked = false;
  /** Catalog adds waiting in `prepareModel`, each until its tap's signal aborts. */
  private adds = new Set<AbortSignal>();
  private parseTimer: unknown = null;
  private playerId: unknown = null;
  private accountId: string | null = null;
  private displayName = '';
  private scene: Scene | null = null;
  private models = new Set<string>();
  private record: PeerDivergence[] = [];
  /** The last parse is behind: the file holds changes it did not read, or the library has measured models since. */
  private unread = false;
  /**
   * rt sends a newcomer's replay as the batch right after its SessionState, and only when there is one (sessionStore.js
   * `admitPlayer`): with none, the first live batch is read as the replay, the safe side.
   */
  private replayNext = false;
  /**
   * By UniqueId, the model a piece wore when the file last held it whole, noted just before its next colour or model
   * change (`EncodeInput.models`); no note, it wears it still. A whole Info carries no size, so it leaves the note.
   */
  private wholeModels = new Map<string, string>();
  private origins = new Map<string, ItemOrigin>();
  private graveyard = new Map<string, GraveyardEntry>();
  private devHandle: { fileXml: () => string | null } | null = null;

  constructor(private readonly deps: LiveSessionDeps) {
    this.presence = new LivePresence({ store: deps.store, connection: deps.connection, clock: deps.clock, file: () => this.file, retry: () => this.retry() });
  }

  get = (): LiveState => this.state;
  subscribe = (fn: () => void): (() => void) => { this.listeners.add(fn); return () => { this.listeners.delete(fn); }; };
  /** What the stored copy does not reproduce since its base: the room's edits that did not land. */
  get divergence(): readonly PeerDivergence[] { return this.record; }
  /** Bumped by each fresh copy of the room and by close. */
  get generation(): number { return this.base; }

  get writable(): boolean { return this.opened && this.editable(); }

  submit(proposal: LocalProposal): Promise<CommitDecision> {
    // A throw would leave the store waiting on this edit forever; the file may already hold its events, so ask for a fresh copy.
    try { return Promise.resolve(this.decide(proposal)); } catch { return Promise.resolve(this.resynced()); }
  }

  /** Reads in the room's events already arrived, so that the store rebases a step on the file the encoder reads (`rebaseItemsPatch`). */
  readArrived(): void {
    if (!this.unread || !this.opened || this.opening || this.closed) return;
    if (this.parseTimer !== null) { this.deps.clock.clearTimeout(this.parseTimer); this.parseTimer = null; this.parseQueued = false; }
    this.publish();
  }

  /** A new piece's `UniqueId`, lowercase as the app writes them, and the internal id the re-read of the file gives it. */
  mintItem(origin?: NewItemOrigin): ItemIdentity {
    const uniqueId = crypto.randomUUID().toLowerCase();
    bounded(this.origins, uniqueId, origin?.copyOf ? { kind: 'copy', copyOf: origin.copyOf } : { kind: 'catalog', pro: !!origin?.pro, variationId: origin?.variationId }, ORIGINS_SIZE);
    return { id: `i:${uniqueId}`, uniqueId };
  }

  /**
   * Readies a model the person is about to add: its encode and read-back take the room library's measured size and pivot.
   * A model the web is still converting is waited for until `signal` aborts; until then the add is under way, and rt's
   * save takes no fresh copy over it.
   */
  async prepareModel(model: ModelRef, signal?: AbortSignal): Promise<boolean> {
    if (signal && !signal.aborted) {
      this.adds.add(signal);
      signal.addEventListener('abort', () => this.adds.delete(signal), { once: true });
    }
    const measured = this.deps.measured;
    if (!measured) return true;
    if (!measured(model.id)) await this.deps.prepareModels([model]).catch(() => undefined);
    while (!measured(model.id) && await this.deps.converting?.(model.id, signal)) await this.deps.prepareModels([model]).catch(() => undefined);
    if (!measured(model.id)) return false;
    this.models.add(model.raw);
    return true;
  }

  explain(code: string): string | undefined {
    if (code === 'off') {
      if (this.closed || !WRITERS.has(this.state.role ?? '') || (this.state.terminal && !RECOVERABLE.has(this.state.terminal))) return undefined;
      return translate(this.state.idless ? '#WebLiveSaveInAppFirst' : '#WebLiveEditWaits');
    }
    return code === 'busy' ? translate('#WebLiveNotConnected') : code === 'conflict' || code === 'invalid' ? translate('#WebLiveConflict') : undefined;
  }

  start(): void {
    const { connection } = this.deps, leaving = this.deps.pageHide ?? windowPageHide;
    this.stops.push(connection.on('batch', events => this.receive(events)),
      connection.on('status', status => this.set({ status })),
      connection.on('terminal', terminal => this.end(terminal)));
    if (leaving) this.stops.push(leaving.subscribe(() => this.handOver()));
    this.pointers = this.deps.pointers ?? pointerHold();
    // A press's own handlers, and the click after it, run before the change they waited for is settled.
    if (this.pointers) this.stops.push(this.pointers.subscribe(() => { if (this.roleWait) this.roleWait.timer ??= this.deps.clock.setTimeout(() => this.settleRole(), 0); }));
    this.stops.push(this.deps.store.subscribe(() => this.queueSettle()));
    if (typeof window !== 'undefined' && import.meta.env?.DEV) {
      this.devHandle = { fileXml: () => this.file?.write() ?? null };
      (window as DevWindow).hpLive = this.devHandle;
    }
    connection.start();
  }

  /** The person's own «reconnect», after an end they may come back from or while the link is down. */
  retry(): void {
    if (this.closed || (this.state.terminal && !RECOVERABLE.has(this.state.terminal))) return;
    this.set({ terminal: null });
    this.deps.connection.retry();
  }

  acknowledgeRole(): void { this.set({ roleChange: null }); }

  /** The «Check again» button (`#CollabRetryCheck`) under the id-less notice: the app's save reaches the web only with a fresh copy of the room. */
  checkIds(): void {
    if (this.closed || this.state.terminal || !this.state.idless || this.state.checking) return;
    this.set({ checking: true });
    this.requestResync(true);
  }

  close(): void {
    if (this.closed) return;
    // Before the close: the browser sends what is queued on the socket ahead of its close frame.
    this.handOver();
    this.closed = true;
    this.base++;
    for (const stop of this.stops.splice(0)) stop();
    this.deps.connection.close();
    this.clearTimers();
    this.dropRoleWait();
    this.presence.dispose();
    if (this.devHandle) { const host = window as DevWindow; if (host.hpLive === this.devHandle) delete host.hpLive; }
    const { store } = this.deps;
    if (!store.isSharedAdapter(this)) return;
    // Without its session the room's scene left in view is a picture: an edit there would reach neither the room nor the browser.
    store.cancelStroke();
    this.closeItemPanels({ edit: 'none' });
    store.attachShared(null);
  }

  private editable(): boolean {
    return this.linkEditable() && WRITERS.has(this.state.role ?? '');
  }

  private linkEditable(): boolean {
    return !this.closed && !this.state.terminal && this.state.status === 'live' && !this.state.idless;
  }

  /**
   * The person's edit, decided in the same task: rt answers nothing, so what the stored file reads after it is the answer.
   * The file takes the events before the socket does: a socket that then refuses them leaves the two apart until a re-sync.
   */
  private decide(proposal: LocalProposal): CommitDecision {
    const { connection, store } = this.deps, file = this.file, patch = proposal.patch;
    if (this.closed || !WRITERS.has(this.state.role ?? '') || (this.state.terminal && !RECOVERABLE.has(this.state.terminal))) return { status: 'rejected', code: 'forbidden' };
    if (!this.writable || this.opening || !file || !this.scene) return { status: 'rejected', code: 'busy' };
    if (!patch) return { status: 'rejected', code: 'invalid', reason: '#WebLiveOnlyInApp' };
    let scene: Scene;
    try { scene = this.unread ? this.deps.parse(file.write()) : this.scene; } catch { return this.resynced('#WebLiveNotConnected'); }
    const rebased = rebaseItemsPatch({ scene, name: this.deps.name }, patch, proposal.kind === 'undo' || proposal.kind === 'redo');
    if (!rebased.ok) return { status: 'rejected', code: rebased.code };
    if (!rebased.value) return { status: 'unchanged', revision: this.revision };
    let encoded: ReturnType<typeof encodeProposal>;
    try {
      encoded = encodeProposal({ file, scene, patch: rebased.value, origins: this.origins, graveyard: this.graveyard, pivots: this.deps.pivots?.(),
        models: this.wholeModels, history: proposal.kind === 'undo' || proposal.kind === 'redo' });
    } catch { return { status: 'rejected', code: 'invalid', reason: '#WebLiveOnlyInApp' }; }
    if ('code' in encoded) return { status: 'rejected', code: encoded.code, reason: encoded.reason, ...naming(encoded.pieces) };
    const left = encoded.held ? { notice: CHANGED_IN_APP, ...naming(encoded.held) } : {};
    if (!encoded.events.length) return { status: 'unchanged', revision: this.revision, ...left };
    if (!connection.canSend) return { status: 'rejected', code: 'busy' };
    this.bury(encoded.buried);
    let landed = false;
    try {
      // A part the app rebuilds and the web does not follow (a colour swap's own light) lands as it does for a co-author's edit.
      const { divergent } = applyPeerEvents(file, encoded.events, this.noteModels(file));
      landed = divergent.every(each => each.reason === 'partial');
      if (landed) this.diverged(divergent);
    } catch { landed = false; }
    this.unread = true;
    if (!landed) return this.resynced('#WebLiveNotConnected');
    if (!connection.send(encoded.events)) return this.resynced();
    if (!this.publish({ commandId: proposal.owner.commandId }) || !store.isSharedAdapter(this)) return this.resynced('#ProjectWillRefreshOnReconnect');
    return { status: 'committed', revision: this.revision, patch: this.readBack(rebased.value), ...left };
  }

  /** Notes a piece's model before the events change it (`wholeModels`); an add brings the piece whole. */
  private noteModels(file: ProjectFile): (event: RtEvent, key: string) => void {
    return (event, key) => {
      if (event.type === 'DomainLifecycle') {
        if ((event.data as { op?: unknown } | undefined)?.op === 'add' && !file.byId(key)) this.wholeModels.delete(key);
        return;
      }
      const [uniqueId, property] = key.split('::');
      const element = property === 'Variation' && !this.wholeModels.has(uniqueId) ? file.byId(uniqueId) : undefined;
      if (element) this.wholeModels.set(uniqueId, furnitureIdOf(file, element));
    };
  }

  /** A refusal that asks for a fresh copy of the room: `busy`, so the step stays in history for the person to try again on it. */
  private resynced(reason?: string): CommitDecision {
    this.requestResync();
    return { status: 'rejected', code: 'busy', ...(reason ? { reason } : {}) };
  }

  /** The step the edit leaves, measured on the file as it reads now: each piece's own fields (`recordedChange`), an added piece as it reads, each floor where the file has it. */
  private readBack(patch: DocumentPatch): DocumentPatch {
    if (patch.kind !== 'units') return patch;
    return { ...patch, floors: patch.floors.map(floor => {
      const slot = this.scene?.levels.findIndex(value => value.uniqueId === floor.architectureBefore.uniqueId) ?? -1, level = this.scene?.levels[slot];
      const items = floor.items.flatMap((change): ItemChange[] => {
        const now = change.after ? level?.items.find(item => item.id === change.after!.id) : undefined;
        if (!change.before) return now ? [{ id: change.id, before: null, after: now }] : [];
        if (!change.after || !now) return [{ id: change.id, before: change.before, after: now ?? null }];
        const step = recordedChange({ ...change, before: change.before, after: change.after }, now, { then: floor.architectureBefore.walls, now: level!.walls });
        return step ? [step] : [];
      });
      if (!level || floor.architectureAfter) return { ...floor, items };
      // A floor a co-author added or removed before this one moves it to another slot, and its `index` with it.
      const { items: _items, ...architectureBefore } = level;
      return { ...floor, slot, architectureBefore, items };
    }) };
  }

  /**
   * Keeps what the file held for the pieces about to go, so that undoing the delete adds them back as they were; a piece
   * leaves once no step left in the store's history can add it back.
   */
  private bury(buried: ReadonlyArray<[string, GraveyardEntry]>): void {
    const kept = this.deps.store.historyPieces;
    for (const uniqueId of [...this.graveyard.keys()]) if (!kept.has(uniqueId)) this.graveyard.delete(uniqueId);
    for (const [uniqueId, entry] of buried) this.graveyard.set(uniqueId, entry);
  }

  private requestResync(immediate = false): void {
    if (!this.closed) this.deps.connection.resync(immediate);
  }

  /** What the store lets the person change follows the link and the role: an edit under way stops the moment either is lost. */
  private syncEdit(): void {
    const { store } = this.deps;
    if (!this.opened || this.closed || !store.isSharedAdapter(this)) return;
    const edit = this.editable() ? 'items' : 'none';
    // The role alone stops editing: a gesture under way runs to its end (`settleRole`).
    if (edit === 'none' && this.roleWait && this.linkEditable()) return;
    if (store.get().edit === edit) return;
    if (edit === 'items') { store.set({ edit }); return; }
    store.cancelStroke();
    this.closeItemPanels({ edit });
  }

  private closeItemPanels(extra: Partial<EditorState> = {}): void { closeItemPanels(this.deps.store, extra); }

  private receive(events: RtEvent[]): void {
    if (this.closed) return;
    const replay = this.replayNext;
    this.replayNext = false;
    let run: RtEvent[] = [];
    const flush = () => { if (run.length) this.applyScene(run, replay); run = []; };
    for (const event of events) {
      if (event.type === 'DomainChange' || event.type === 'DomainLifecycle') { run.push(event); continue; }
      // Drag frames carry nothing the file keeps (a move ends in a DomainChange); skipped, they never split the replay's look-ahead.
      if (event.type === 'DomainTransform') continue;
      if (PRESENCE.has(event.type)) { this.presence.receive(event); continue; }
      flush();
      if (event.type === 'SessionState') this.rebase(event);
      else if (event.type === 'PullFullState') this.answer(event);
      else if (event.type === 'RoleChanged') this.roleChanged(event);
      else if (event.type === 'SceneSaved') this.saved();
      else if (event.type === 'SessionClosed') this.end('closed');
    }
    flush();
  }

  private rebase(state: RtEvent): void {
    const xml = typeof state.projectXml === 'string' ? state.projectXml : '';
    const presence = Array.isArray(state.presence) ? state.presence as Array<{ playerId?: unknown; accountId?: unknown; userName?: unknown }> : [];
    const me = presence.find(person => person.playerId === state.playerId);
    this.playerId = state.playerId;
    this.accountId = typeof me?.accountId === 'string' ? me.accountId : this.accountId;
    this.displayName = typeof me?.userName === 'string' ? me.userName : this.displayName;
    this.presence.rebase(state);
    if (typeof state.role === 'string') this.takeRole(state.role);
    let file: ProjectFile | null = null;
    try { if (xml.trim()) file = ProjectFile.read(xml); } catch { file = null; }
    if (!file) {
      // An open scene stays and asks again later; an empty base on the way in (rt could not fetch it yet) may come next time.
      if (this.opened) this.deps.connection.resync();
      else if (xml.trim()) this.end('unreadable');
      else { this.deps.connection.close(); this.end('unreachable'); }
      return;
    }
    this.base++;
    this.file = file;
    this.unread = true;
    this.record = [];
    this.replayNext = true;
    const named = this.deps.store.historyPieces;
    // A graveyard copy was buried from the older file: its note stays, or bringing the piece back would send the old model's size.
    for (const uniqueId of [...this.wholeModels.keys()]) if (!named.has(uniqueId) || (file.byId(uniqueId) && !this.graveyard.has(uniqueId))) this.wholeModels.delete(uniqueId);
    this.clearTimers();
    const appVersion = Number(file.element([]).attribute('Revision')) === APP_REVISION;
    if (appVersion) this.versionAsked = false;
    this.set({ idless: file.idless, checking: false, faithful: appVersion, otherVersion: !appVersion });
    if (this.opened) this.requestParse();
    else void this.open();
  }

  private async open(): Promise<void> {
    const base = this.base;
    this.opening = true;
    try {
      const draft = this.deps.parse(this.file!.write());
      const fresh = this.freshModels(draft);
      if (fresh.length) await this.deps.prepareModels(fresh).catch(() => undefined);
    } catch {
      if (base === this.base) this.end('unreadable');
      return;
    } finally {
      if (base === this.base) this.opening = false;
    }
    // A newer base opened meanwhile without these models' data: it is read again now that they are ready.
    if (base === this.base) this.publish();
    else { this.unread = true; this.requestParse(); }
  }

  private applyScene(events: RtEvent[], replay: boolean): void {
    if (!this.file) return;
    const result = applyPeerEvents(this.file, events, this.noteModels(this.file), { replay });
    this.diverged(result.divergent);
    if (result.applied) { this.unread = true; this.requestParse(); }
    if (result.unknownIds.length && this.resyncTimer === null && this.file.idless) {
      this.resyncTimer = this.deps.clock.setTimeout(() => { this.resyncTimer = null; if (!this.closed) this.deps.connection.resync(); }, RESYNC_DELAY_MS);
    }
  }

  /** What the copy no longer reproduces, recorded: from now until the next base it is not the room's file. */
  private diverged(entries: readonly PeerDivergence[]): void {
    if (!entries.length) return;
    this.record.push(...entries);
    this.set({ faithful: false });
  }

  /** rt saved the room (`SceneSaved`, rt persist.js): a copy that is not faithful takes the saved file as its base, one at another file version once. */
  private saved(): void {
    const { state } = this;
    if (state.faithful || this.refreshTimer !== null || (state.otherVersion && this.versionAsked)) return;
    this.refresh();
  }

  /**
   * Rejoins for rt's saved file once the rejoin, which turns editing off, cuts no stroke or catalog add and closes no
   * properties panel, typed text and all. An id-less re-sync still armed would drop the new socket, so it goes first.
   */
  private refresh(): void {
    this.refreshTimer = null;
    const { state, deps } = this;
    if (this.closed || state.terminal || state.status !== 'live') return;
    if (this.writable && (deps.store.documentBusy || this.adds.size > 0 || inspected(deps.store.get()))) {
      this.refreshTimer = deps.clock.setTimeout(() => this.refresh(), REFRESH_POLL_MS);
      return;
    }
    if (state.otherVersion) this.versionAsked = true;
    if (this.resyncTimer !== null) { deps.clock.clearTimeout(this.resyncTimer); this.resyncTimer = null; }
    this.requestResync(true);
  }

  /**
   * The room's file when this copy may stand for it, else null. A file the last parse is behind (a hidden tab runs no
   * frames) is read once first: one that does not read is never sent.
   */
  private snapshot(): string | null {
    const { state } = this;
    if (!state.faithful || !this.opened || this.opening || this.closed || state.terminal || state.status !== 'live' || !WRITERS.has(state.role ?? '')) return null;
    if (!this.deps.connection.canSend || !this.file) return null;
    const xml = this.file.write();
    if (this.unread) {
      try { this.deps.parse(xml); } catch { this.diverged([{ key: '', reason: 'unreadable' }]); return null; }
    }
    return xml;
  }

  /** rt's pull for the scene it saves, answered as the app's `ProjectStateSync.RespondToPullFullState` does, from a faithful copy only. */
  private answer(pull: RtEvent): void {
    const requestId = (pull.data as { requestId?: unknown } | undefined)?.requestId;
    if (typeof requestId !== 'string') return;
    const projectXml = this.snapshot();
    if (projectXml !== null) this.deps.connection.send([{ type: 'FullStateSnapshot', data: { requestId, projectXml } }]);
  }

  /** The room's file, unasked, as the web leaves: rt drops it until it takes one. */
  private handOver(): void {
    const projectXml = this.snapshot();
    if (projectXml !== null) this.deps.connection.send([{ type: 'FullStateSnapshot', data: { projectXml } }]);
  }

  private requestParse(): void {
    if (this.parseQueued || this.opening || !this.opened || this.closed) return;
    this.parseQueued = true;
    const go = () => this.deps.schedule(() => { this.parseQueued = false; if (this.unread) this.publish(); });
    const wait = this.lastParseMs > SLOW_PARSE_MS ? this.lastParseAt + SLOW_PARSE_GAP_MS - this.deps.clock.now() : 0;
    if (wait > 0) this.parseTimer = this.deps.clock.setTimeout(() => { this.parseTimer = null; go(); }, wait);
    else go();
  }

  /** `owner`: the person's own edit this read answers. False when the file did not read or the store has gone on. */
  private publish(owner?: DocumentOwner): boolean {
    const { store, projectId, name, clock } = this.deps;
    if (!this.file || this.closed) return false;
    // The store went on to another project: this session has nothing left to show.
    if (this.opened && (!store.isSharedAdapter(this) || store.get().projectId !== projectId)) { this.close(); return false; }
    const started = clock.now();
    let scene: Scene;
    try { scene = this.deps.parse(this.file.write()); } catch { this.diverged([{ key: '', reason: 'unreadable' }]); return false; }
    this.unread = false;
    this.lastParseAt = clock.now();
    this.lastParseMs = this.lastParseAt - started;
    scene = this.share(scene);
    this.scene = scene;
    if (!this.opened) {
      store.loadScene(scene, projectId, name, 0, { edit: this.editable() ? 'items' : 'none' });
      store.attachShared(this);
      this.opened = true;
    }
    const snapshot = { documentId: projectId, revision: ++this.revision, layoutVersion: 0, generation: 0, scene, name, members: [],
      self: { userId: this.accountId ?? String(this.playerId ?? ''), role: this.state.role === 'owner' ? 'owner' as const : 'editor' as const, displayName: this.displayName } };
    store.acceptShared(snapshot, owner);
    this.presence.accept(snapshot);
    if (this.revision === 1) this.deps.onOpen?.();
    const fresh = this.freshModels(scene);
    if (fresh.length) void this.deps.prepareModels(fresh).catch(() => undefined).then(() => { this.unread = true; this.requestParse(); });
    return true;
  }

  /** Untouched levels, walls, openings and items keep the previous objects, matched by id, so the 3D view rebuilds only what changed. */
  private share(next: Scene): Scene {
    const previous = this.scene;
    if (!previous) return next;
    const levels = next.levels.map((level, index) => {
      const before = (level.uniqueId && previous.levels.find(other => other.uniqueId === level.uniqueId)) || previous.levels[index];
      if (!before) return level;
      const items = alignById(before.items, level.items), walls = alignById(before.walls, level.walls), openings = alignById(before.openings, level.openings);
      const aligned: Level = items === before.items && walls === before.walls && openings === before.openings ? before : { ...before, items, walls, openings };
      return shareEqual(aligned, level);
    });
    return shareEqual(previous, { ...next, levels });
  }

  private freshModels(scene: Scene): ModelRef[] {
    const fresh = scene.assets.models.filter(model => !this.models.has(model.raw));
    for (const model of fresh) this.models.add(model.raw);
    return fresh;
  }

  private roleChanged(event: RtEvent): void {
    const accountId = typeof event.accountId === 'string' ? event.accountId : '';
    if (accountId && accountId !== this.accountId) return;
    // A change of the link's role is for those in by the link (the app's SessionController.HandleRoleChanged).
    if (!accountId && this.state.role === 'owner') return;
    if (event.newRole === 'no_access') { this.end('closed'); return; }
    if (typeof event.newRole !== 'string') return;
    // rt drops an edit that reaches it after the demotion, while the web's file already holds it.
    this.takeRole(event.newRole, true);
  }

  /** The role from a RoleChanged or a fresh copy, whose own event a dropped socket may have missed. Editing stops at once;
   *  a stroke under way runs to its refused end before the change is told, as the app's MultiplayerRoleChangeController waits. */
  private takeRole(role: string, resync = false): void {
    const could = WRITERS.has(this.state.role ?? ''), can = WRITERS.has(role), { store } = this.deps;
    const changed = could !== can && this.opened && !this.closed && store.isSharedAdapter(this);
    if (changed) {
      const wait = this.roleWait ??= { tell: 'view', resync: false, queued: false, timer: null };
      wait.tell = can ? 'edit' : 'view';
      wait.resync ||= !can && resync;
    }
    this.set({ role });
    if (changed) this.settleRole();
  }

  /** Runs once the stroke's own commit is decided: the store tells its end before it submits. */
  private queueSettle(): void {
    const wait = this.roleWait;
    if (!wait || wait.queued) return;
    wait.queued = true;
    queueMicrotask(() => { wait.queued = false; if (this.roleWait === wait) this.settleRole(); });
  }

  private settleRole(): void {
    const wait = this.roleWait;
    if (!wait || this.closed) return;
    if (wait.timer !== null) { this.deps.clock.clearTimeout(wait.timer); wait.timer = null; }
    if (this.deps.store.stroking || this.pointers?.held()) return;
    this.roleWait = null;
    if (!WRITERS.has(this.state.role ?? '')) this.closeItemPanels({ edit: 'none' });
    this.set({ roleChange: wait.tell });
    if (wait.resync) this.requestResync();
  }

  private dropRoleWait(): void {
    if (this.roleWait?.timer != null) this.deps.clock.clearTimeout(this.roleWait.timer);
    this.roleWait = null;
  }

  private end(terminal: LiveTerminal): void {
    if (this.closed || (this.state.terminal && !RECOVERABLE.has(this.state.terminal))) return;
    if (!RECOVERABLE.has(terminal)) { this.deps.connection.close(); this.clearTimers(); }
    this.presence.clear();
    this.set({ terminal, checking: false });
  }

  private clearTimers(): void {
    if (this.resyncTimer !== null) this.deps.clock.clearTimeout(this.resyncTimer);
    if (this.refreshTimer !== null) this.deps.clock.clearTimeout(this.refreshTimer);
    if (this.parseTimer !== null) this.deps.clock.clearTimeout(this.parseTimer);
    this.resyncTimer = null; this.refreshTimer = null; this.parseTimer = null;
    this.parseQueued = false;
  }

  private set(patch: Partial<LiveState>): void {
    if (Object.entries(patch).every(([key, value]) => this.state[key as keyof LiveState] === value)) return;
    this.state = { ...this.state, ...patch };
    this.presence.setStatus(this.state.status, !!this.state.terminal);
    this.syncEdit();
    for (const fn of [...this.listeners]) fn();
  }
}
